import { type ColumnVO, type IServerSideGetRowsRequest } from 'ag-grid-community';
import findKey from 'lodash/findKey';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import replace from 'lodash/replace';

import { composeQuery, select, c, prerenderedSelect, orderBy, f } from 'common/soql_builder';
import { type NamedSelect } from 'common/soql_builder/select';
import { type ViewColumn } from 'common/types/viewColumn';

import { type ColumnAggregation } from '../dataProviders/types';
import { type CustomSortConfig, type HierarchyColumnConfig, type OrderConfig } from '../vif';
import { GROUP_COLUMN_PREFIX } from '../views/agGridReact/Constants';

import { isPivotInAgRequest } from './PivotHelpers';
import { measureAlias } from './SoqlHelpers';
import { findCorrespondingColumn } from './TableDataColumnHelpers';
import { buildCasesParts } from './TableDataHelpers';
import {
  createMeasureAliasMap,
  createSelectAliasMap,
  getSelectAggregationsQuery,
} from './TableDataQueryHelpers';
import { isCustomSortEnabled, isTableAdvancedTotalingEnabled } from './VifSelectors';
import {
  BACKTICK_WRAPPED_TEXT,
  DIV_ZERO_SENTINEL,
} from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';
import { I18n } from 'common/i18n';
import { type Expr } from 'common/types/soql';
import { protectDivisions, escapeSoqlStringLiteral } from './safeDivisionHelpers';

export interface CascadingQueryConfig {
  columns: ViewColumn[];
  legacyWhereClause: string;
  agRequest: IServerSideGetRowsRequest;
  currentQueryGroupLevel: number;
  hierarchyConfigs?: HierarchyColumnConfig[];
  nonStandardAggregations?: ColumnAggregation[] | null;
  whereParts?: Expr | undefined;
  pageSize?: number;
  startIndex?: number;
  order?: OrderConfig[];
  searchString?: string;
  customSortConfig?: CustomSortConfig;
  isExportQuery?: boolean;
}

/**
 * Recursive Sub-Tree Generator: Constructs the deeply nested conditional logic required
 * for calculating custom Advanced Totaling formulas at the leaf-node level.
 *
 * **Architecture & Purpose:**
 *
 * Rather than calculating mathematical formulas in the browser (which is subject to pagination limits),
 * this compiler translates user-generated strings (e.g. `` `USA` + (`Canada` * 2) ``) directly into a
 * valid SoQL Abstract Syntax Tree (AST). It acts as a recursive descent parser.
 *
 * **Execution Flow:**
 *
 * 1. Scans the `customFormula` string for backtick-wrapped tokens using a regex replacer.
 * 2. Translates each matched token into a strict `IS NULL` or `=` conditional statement.
 * 3. Injects accumulated parent constraints (to accurately scope calculations within multi-level hierarchies).
 * 4. Yields a highly-specific `SUM(CASE(...))` statement for every variable, allowing the database
 * engine to safely execute PEMDAS math between the resulting cases.
 *
 * @param {number} configIdx - The current depth index within `activeConfigs` being processed.
 * @param {ViewColumn} viewCol - The data layer representation of the measure column being calculated.
 * @param {string} aggFunc - The aggregation method (e.g., 'sum'). Note: Advanced Totaling strictly assumes SUM context.
 * @param {HierarchyColumnConfig[]} activeConfigs - Filtered array of hierarchy layers that actually contain formulas.
 * @param {string[]} [parentConditions=[]] - Accumulated SoQL string conditions from higher up the recursion tree.
 *
 * @returns {string} The fully compiled SoQL `CASE` snippet, securely wrapping the token's logic.
 *
 * @example
 * // If `Canada` is found inside the formula for 'Country', and we are nested under 'North America':
 *
 * // Resulting compilation:
 * ```sql
 * sum(
 *  case(
 *    `Region` = 'North America' AND `Country` = 'Canada',
 *    `Sales`
 *    true,
 *    0
 *  )
 * )
 * ```
 */
export const buildNestedExpression = (
  configIdx: number,
  viewCol: ViewColumn,
  aggFunc: string,
  activeConfigs: HierarchyColumnConfig[],
  parentConditions: string[] = [],
): string => {
  const noValueKeyword = I18n.t('summary_table.no_value', { scope: 'shared.visualizations.charts.common' });

  const currentConfig = activeConfigs[configIdx];
  const rawFormula = currentConfig.customFormula ?? '';

  // wrap any division operators in safe soql (nullif + case) before we do the variable substitution.
  // this prevents divide-by-zero from crashing the database at query time.
  const safeFormula = protectDivisions(rawFormula);

  // Replace the backticked IDs in the custom formula (e.g., `USA` + `Canada`)
  return safeFormula.replace(BACKTICK_WRAPPED_TEXT, (_, formulaIdValue) => {
    const escapedValue = escapeSoqlStringLiteral(formulaIdValue);
    // Create condition: Handle (No Value) as NULL, otherwise use equality check (e.g. `column_name` = 'formulaIdValue')
    const currentCondition =
      formulaIdValue === noValueKeyword
        ? `\`${currentConfig.columnName}\` IS NULL`
        : `\`${currentConfig.columnName}\` = '${escapedValue}'`;
    const allConditions = [...parentConditions, currentCondition];

    // If there is another level below with a formula, recurse
    if (configIdx + 1 < activeConfigs.length) {
      return `(${buildNestedExpression(configIdx + 1, viewCol, aggFunc, activeConfigs, allConditions)})`;
    }

    // LEAF NODE: Construct the final case statement
    const conditionStr = allConditions.join(' AND ');
    const measureIdentifier = `\`${viewCol.fieldName}\``;

    // Type-specific handling for Checkboxes and URLs is not typically required inside
    // sum/math formulas, so we use standard identifiers.
    return `${aggFunc}(case(${conditionStr}, ${measureIdentifier}, true, 0))`;
  });
};

/**
 * Generates a list of SoQL SELECT clauses for table aggregations, utilizing a **Hybrid Strategy**
 * to support complex custom formulas ("Advanced Totaling") alongside standard aggregations.
 *
 * 1. **Path A (Advanced Totaling):**
 * - Triggered **ONLY** when `aggFunc === 'sum'` AND there are active `hierarchyConfigs` starting from `startLevelIndex`.
 * - Uses recursive logic to parse custom formulas (e.g., `` `US` + `CA` ``).
 * - Replaces formula tokens with nested `case` statements that enforce hierarchy logic down to the leaf node.
 * - Generates raw SoQL strings to handle arithmetic ( `+`, `-`, `*`, `/`) defined in the formula.
 *
 * 2. **Path B (Standard Fallback):**
 * - Triggered for all other aggregation types (`avg`, `min`, `max`, `count`) OR if no formulas exist for the current level.
 * - Replicates standard SoQL generation logic, including special handling for `checkbox` (boolean casting) and `url` types.
 * - Supports `nonStandardAggregations` for custom-defined aggregation types.
 *
 * @param {ColumnVO[]} valueCols - The columns to be aggregated (e.g., `[{ id: 'sales', aggFunc: 'sum' }]`).
 * @param {ViewColumn[]} columns - Metadata for all available columns (used to determine data types like checkbox/url).
 * @param {HierarchyColumnConfig[]} configs - The hierarchy configuration containing `customFormula` definitions for each level.
 * @param {number} startLevelIndex - The index in `configs` to start processing from.
 * - Pass `groupKeys.length` to apply formulas for the *current* level (filtering the rows themselves).
 * - Pass `groupKeys.length + 1` to apply formulas for the *child* level (bubbling logic up to the parent group).
 * @param {ColumnAggregation[] | null} [nonStandardAggregations] - Optional definitions for custom aggregation types not natively supported by SoQL builder.
 *
 * @returns {NamedSelect[]} An array of SoQL SELECT objects ready for the query builder.
 *
 * @example
 * // =========================================================================
 * // SCENARIO 1: Single Level Formula (Simple Sum)
 * // =========================================================================
 * // Config: Country column has formula: "`US` + `CA`"
 * // Request: SUM('sales')
 * //
 * // Output SoQL:
 * // (
 * //   sum(case(`country` = 'US', `sales`, true, 0)) +
 * //   sum(case(`country` = 'US', `sales`, true, 0)) +
 * //   sum(case(`country` = 'CA', `sales, true, 0))
 * // ) as __measure_alias__
 *
 * @example
 * // =========================================================================
 * // SCENARIO 2: Multi-Level Recursive
 * // =========================================================================
 * // Context: We are calculating the Grand Total (Level 0).
 * // Level 0 Config (Country): Formula is "`NorthAM`" (acting as a wrapper/alias).
 * // Level 1 Config (State):   Formula is "`NY` - `NJ`".
 * // Request: SUM('revenue')
 * //
 * // Logic:
 * // 1. Matches `NorthAM` token in Country formula. Generates `country`='NorthAM'.
 * // 2. Recurses to State config. Matches `NY` and `NJ`.
 * // 3. Injects Parent condition (`country`='NorthAM') into Child conditions.
 * //
 * // Output SoQL:
 * // (
 * //   (
 * //     sum(case(`country` = 'NorthAM' AND `state` = 'NY', `revenue`, true, 0)) -
 * //     sum(case(`country` = 'NorthAM' AND `state` = 'NJ`, `revenue`, true, 0))
 * //   )
 * // ) as __measure_alias__
 *
 * @example
 * // =========================================================================
 * // SCENARIO 3: Multiple Aggregations (Mixed Types)
 * // =========================================================================
 * // Config: Country = "`US` + `UK`"
 * // Request:
 * //   1. SUM('sales')  -> Should use Advanced Totaling formula.
 * //   2. AVG('profit') -> Should use Standard SoQL (cannot sum averages arithmetically).
 * //
 * // Output SoQL (Array of 2 Selects):
 * // [
 * //   1. (sum(case(`country` = 'US', `sales`, true, 0)) + sum(case(`country` = 'UK', `sales`, true, 0)) as alias_0,
 * //   2. avg(`profit`) as alias_1
 * // ]
 *
 * @example
 * // =========================================================================
 * // SCENARIO 4: Arithmetic & Constants
 * // =========================================================================
 * // Config: Region = "(`East` * 1.5) + `West`"
 * // Request: SUM('goals')
 * //
 * // Logic: The recursion preserves the string literals (* 1.5, +) found in the formula.
 * //
 * // Output SoQL:
 * // (
 * //   (sum(case(`region` = 'East', `goals`, true, 0)) * 1.5) +
 * //   sum(case(`region` = 'West', `goals`, true, 0))
 * // ) as __measure_alias__
 *
 */
export const getAdvancedTotalingAggregations = (
  valueCols: ColumnVO[],
  columns: ViewColumn[],
  configs: HierarchyColumnConfig[],
  startLevelIndex: number,
  nonStandardAggregations?: ColumnAggregation[] | null,
): NamedSelect[] => {
  // Only process configs from the specified start level downwards
  const activeConfigs = configs.slice(startLevelIndex).filter((c) => c.isGrouping && !!c.customFormula);

  return valueCols
    .filter((col) => !!col.aggFunc)
    .map((col, idx) => {
      const viewCol = findCorrespondingColumn(columns, { id: col.id });
      if (!viewCol) {
        return null;
      }

      // -----------------------------------------------------------------------
      // PATH A: ADVANCED TOTALING (SUM Only)
      // -----------------------------------------------------------------------
      // We only apply the logic if it's a SUM and we have formulas to apply.
      if (col.aggFunc === 'sum' && activeConfigs.length > 0) {
        const complexExpression = buildNestedExpression(0, viewCol, col.aggFunc, activeConfigs);
        return prerenderedSelect(complexExpression, measureAlias(idx));
      }

      // -----------------------------------------------------------------------
      // PATH B: STANDARD FALLBACK (Everything else)
      // This logic replicates getSelectAggregationsQuery to support AVG, MIN, MAX, etc.
      // -----------------------------------------------------------------------

      // @ts-expect-error implicit any index
      if (typeof f[col.aggFunc] === 'function') {
        // Postgresql does not allow for min or max of boolean columns,
        // so we need to use this work around.
        if (viewCol?.dataTypeName === 'checkbox' && (col.aggFunc === 'max' || col.aggFunc === 'min')) {
          return select(f.eq(f[col.aggFunc](f.iif(viewCol, 1, 0)), 1), measureAlias(idx));
        } else if (viewCol?.dataTypeName === 'url' && (col.aggFunc === 'max' || col.aggFunc === 'min')) {
          return select(f.url(f[col.aggFunc](f.url_url(viewCol)), null), measureAlias(idx));
        }

        // @ts-expect-error implicit any index
        return select(f[col.aggFunc](viewCol), measureAlias(idx));
      } else if (nonStandardAggregations) {
        // in the case of a non-standard aggregation, we need to use the nonStandardAggregations
        // list to find the correct SoQL expression for this column/aggregationType
        const aggregationDefinition = nonStandardAggregations.find(
          (agg) => agg.fieldName === col.id && agg.aggregationType === col.aggFunc,
        );
        if (aggregationDefinition) {
          return prerenderedSelect(aggregationDefinition.soqlExpression, measureAlias(idx));
        }
      }

      return null;
    })
    .filter((s): s is NamedSelect => !!s);
};

/**
 * Calculates the exact Execution Boundary for the active custom formula within the hierarchy tree.
 *
 * **The Paradigm of Cascading Formulas:**
 *
 * A custom formula fundamentally alters the mathematical reality of its *parent* node, not itself.
 * If a user places `` `East` * `West` `` on Level 1 (Region), that math must evaluate per-node
 * at Level 0 (Country). Therefore, a formula existing physically at index `X` logically evaluates
 * and executes at `X - 1`.
 *
 * By identifying this critical boundary, we can dynamically intercept any queries aimed above it
 * and enforce a subquery sandbox to prevent distributive property errors (Sum of Products vs. Product of Sums).
 *
 * @param {HierarchyColumnConfig[]} [hierarchyConfigs] - The full array of active hierarchy group columns.
 * @returns {number} The required evaluation depth. Returns `-1` if the execution boundary applies to the Grand Total
 * or if no advanced formulas are present in the grid.
 */
export const getEvaluationLevel = (hierarchyConfigs?: HierarchyColumnConfig[]): number => {
  if (!hierarchyConfigs) {
    return -1;
  }
  const colWithFormulaIndex = hierarchyConfigs.findIndex((config) => {
    return config.isGrouping && config.customFormula;
  });
  // Subtract 1 level as described in the comment above
  return colWithFormulaIndex === -1 ? -1 : colWithFormulaIndex - 1;
};

/**
 * Generates the SoQL payload for the **Inner Subquery Context**
 *
 * **Design Rationale:**
 * When dealing with complex user-defined formulas involving multiplication or division, executing a flat
 * `SELECT` statement across a highly aggregated view (like the Grand Total) causes catastrophic math errors.
 * `SUM(A * B)` != `SUM(A) * SUM(B)`.
 *
 * To fix this, `buildInnerQueryParams` acts as a strict execution environment. It forces the database
 * engine to group down to the *exact* granular depth where the formula evaluates (the `evaluationLevel`).
 *
 * It runs the raw PEMDAS calculation here, generating perfect mathematical output for each parent node,
 * independent of whatever view AgGrid actually requested.
 *
 * @param {CascadingQueryConfig} config - The master configuration object passed down from the query builder.
 * @param {number} evaluationLevel - The strict hierarchy depth where the grouping and formula execution must occur.
 * @param {{ [key: string]: string }} selectAliasMap - Safely mapped alias mapping to protect against namespace collisions.
 *
 * @returns {Object} A parameters object strictly structured for injection into `soql_builder`'s `composeQuery`.
 */
export const buildInnerQueryParams = (
  config: CascadingQueryConfig,
  evaluationLevel: number,
  selectAliasMap: { [key: string]: string },
) => {
  const {
    agRequest,
    columns,
    hierarchyConfigs,
    nonStandardAggregations,
    whereParts,
    legacyWhereClause,
    searchString,
  } = config;
  const { rowGroupCols, pivotCols, valueCols } = agRequest;
  const isPivoting = isPivotInAgRequest(agRequest);

  const innerGroupCols = rowGroupCols.slice(0, evaluationLevel + 1);
  const innerGroups = innerGroupCols
    .map((col) => findCorrespondingColumn(columns, col))
    .filter((col): col is ViewColumn => !!col);

  const innerSelects: NamedSelect[] = [];

  if (isPivoting) {
    pivotCols.forEach((pivotCol) => {
      const pivotColId = findCorrespondingColumn(columns, pivotCol);
      if (pivotColId) {
        innerSelects.push(select(pivotColId, selectAliasMap[pivotCol.id]));
      }
    });
  }

  // For export queries, include ALL columns from level 0 to currentQueryGroupLevel
  if (config.isExportQuery) {
    for (let i = 0; i <= config.currentQueryGroupLevel; i++) {
      const col = rowGroupCols[i];
      if (col) {
        const viewCol = findCorrespondingColumn(columns, col);
        if (viewCol) {
          innerSelects.push(select(viewCol, selectAliasMap[col.id]));
        }
      }
    }
  } else {
    innerGroupCols.forEach((col) => {
      const viewCol = findCorrespondingColumn(columns, col);
      if (viewCol) {
        innerSelects.push(select(viewCol, selectAliasMap[col.id]));
      }
    });
  }

  const startLevelIndex = evaluationLevel + 1;
  let innerMeasures: NamedSelect[] = [];

  if (isTableAdvancedTotalingEnabled() && hierarchyConfigs) {
    innerMeasures = getAdvancedTotalingAggregations(
      valueCols,
      columns,
      hierarchyConfigs,
      startLevelIndex,
      nonStandardAggregations,
    );
  }

  if (innerMeasures.length === 0) {
    innerMeasures = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
  }

  innerSelects.push(...innerMeasures);

  const groupColumns = [
    ...(isPivoting
      ? pivotCols
          .map((pivotCol) => findCorrespondingColumn(columns, pivotCol))
          .filter((column): column is ViewColumn => !!column)
      : []),
    ...innerGroups,
  ];

  return {
    selects: innerSelects,
    where: whereParts,
    groups: groupColumns,
    search: searchString,
    legacyWhereClause,
  };
};

/**
 * Generates the SoQL payload for the **Outer Query Context**
 *
 * **Design Rationale:**
 *
 * Because `buildInnerQueryParams` already ran the complex math perfectly at a granular level,
 * this outer query is entirely agnostic to the user's custom formulas. It treats the dataset yielded
 * by the piped inner query as flat, materialized numbers.
 *
 * Its sole responsibility is to look at the grouping level AgGrid *actually* requested
 * (`currentQueryGroupLevel`), group the inner data by that target layer, and blindly `SUM()`
 * the numeric column aliases. This architecture completely circumvents SoQL division-by-zero crashes
 * resulting from overly broad aggregations, ensuring 100% stable reporting.
 *
 * *Note on AST Bypass:* We explicitly use `string[]` for `outerGroups` rather than casting fake `ViewColumn`
 * objects. This forces the `soql_builder`'s compiler to encode the string alias safely via `encodeStringAsColumnRef`,
 * preventing the infamous `"Unhandled Case."` runtime crash.
 *
 * @param {CascadingQueryConfig} config - The master configuration object passed down from the query builder.
 * @param {{ [key: string]: string }} selectAliasMap - Alias map needed to accurately reference the inner query's projection layer.
 *
 * @returns {Object} A safely typed object mapping specifically formatted to complete the outer edge of a `c.pipe()`.
 */
export const buildOuterQueryParams = (
  config: CascadingQueryConfig,
  selectAliasMap: { [key: string]: string },
) => {
  const { agRequest, currentQueryGroupLevel, order, pageSize, startIndex, customSortConfig } = config;
  const { rowGroupCols, pivotCols, valueCols } = agRequest;
  const isPivoting = isPivotInAgRequest(agRequest);

  // Checking if AgGrid is actively grouping on this view
  const isDoingGrouping = rowGroupCols.length > currentQueryGroupLevel;

  const outerSelects: NamedSelect[] = [];
  const outerGroups: string[] = [];

  if (isPivoting) {
    pivotCols.forEach((p) => {
      const alias = selectAliasMap[p.id];
      outerSelects.push(prerenderedSelect(`\`${alias}\``, alias));
      outerGroups.push(alias);
    });
  }

  // For export queries, select ALL columns from level 0 to currentQueryGroupLevel
  if (isDoingGrouping && currentQueryGroupLevel >= 0) {
    if (config.isExportQuery) {
      // Select all columns from level 0 to currentQueryGroupLevel for export
      for (let i = 0; i <= currentQueryGroupLevel; i++) {
        const selectCol = rowGroupCols[i];
        if (selectCol) {
          const alias = selectAliasMap[selectCol.id];
          outerSelects.push(prerenderedSelect(`\`${alias}\``, alias));
          outerGroups.push(alias);
        }
      }
    } else {
      const selectCol = rowGroupCols[currentQueryGroupLevel];
      const alias = selectAliasMap[selectCol.id];
      outerSelects.push(prerenderedSelect(`\`${alias}\``, alias));
      outerGroups.push(alias);
    }
  }

  valueCols
    .filter((col) => !!col.aggFunc)
    .forEach((col, idx) => {
      const mAlias = measureAlias(idx);
      const func = col.aggFunc === 'sum' || isTableAdvancedTotalingEnabled() ? 'sum' : col.aggFunc;

      if (isTableAdvancedTotalingEnabled()) {
        // if any inner row returned the divide-by-zero sentinel, propagate it up.
        // without this, the outer sum() would add the sentinel to real values and
        // produce a wildly wrong number instead of showing "Error" at the parent level.
        const sentinelAwareAgg = `case(min(\`${mAlias}\`) = ${DIV_ZERO_SENTINEL}, ${DIV_ZERO_SENTINEL}, true, ${func}(\`${mAlias}\`))`;
        outerSelects.push(prerenderedSelect(sentinelAwareAgg, mAlias));
      } else {
        outerSelects.push(prerenderedSelect(`${func}(\`${mAlias}\`)`, mAlias));
      }
    });

  const outerOrders = order
    ? order
        .map((o: OrderConfig) => {
          const fieldName = replace(o.columnName, `${GROUP_COLUMN_PREFIX}-`, '');
          const ordering = o.ascending === true ? 'ascending' : 'descending';

          if (isDoingGrouping && currentQueryGroupLevel >= 0) {
            const selectCol = rowGroupCols[currentQueryGroupLevel];
            if (fieldName === selectCol.id) {
              return orderBy(selectAliasMap[selectCol.id], ordering);
            }
          }

          const measureAliasMap = createMeasureAliasMap(agRequest);
          const measureAliasKey = findKey(measureAliasMap, (fn) => fn === fieldName);
          if (!isUndefined(measureAliasKey)) {
            return orderBy(measureAliasKey, ordering);
          }
          return undefined;
        })
        .filter((orderForSoql) => !isUndefined(orderForSoql))
    : [];

  return {
    selects: outerSelects,
    groups: outerGroups,
    orders: outerOrders,
    limit: pageSize,
    offset: startIndex,
    ...(isCustomSortEnabled() &&
      !isEmpty(customSortConfig) && {
        cases: buildCasesParts(outerSelects, customSortConfig, order),
      }),
  };
};

// Remove all backtick-wrapped text (Example: `Asset/Liability` * `Equity*Asset` / `Liability*Equity`  -> ' * / ' )
// Then we check the new string if it contains asterisk (multiplication) or slash (division)
export const hasMultiplyOrDivide = (activeConfig: HierarchyColumnConfig) => {
  const formulaWithoutBackticks = activeConfig.customFormula!.replace(BACKTICK_WRAPPED_TEXT, '');
  return formulaWithoutBackticks.includes('*') || formulaWithoutBackticks.includes('/');
};

/**
 * EXPORT-SPECIFIC: Creates a select alias map for export queries that includes all columns
 * from level 0 to currentQueryGroupLevel (unlike standard createSelectAliasMap)
 */
function createExportSelectAliasMap(
  agRequest: IServerSideGetRowsRequest,
  columns: ViewColumn[],
  currentQueryGroupLevel: number,
): { [key: string]: string } {
  const { rowGroupCols, pivotCols } = agRequest;
  const isPivoting = isPivotInAgRequest(agRequest);
  const aliasMap: { [key: string]: string } = {};
  let aliasCount = 0;

  if (isPivoting) {
    pivotCols.forEach((pivotCol) => {
      aliasMap[pivotCol.id] = aliasCount === 0 ? '__select_alias__' : `__select_alias_${aliasCount}__`;
      aliasCount += 1;
    });
  }

  for (let i = 0; i <= currentQueryGroupLevel; i++) {
    const col = rowGroupCols[i];
    if (col) {
      aliasMap[col.id] = i === 0 ? '__select_alias__' : `__select_alias_${i}__`;
    }
  }

  return aliasMap;
}

/**
 * This is the entry point for handling hierarchical data requests. It performs a fast, zero-cost
 * topological analysis of the incoming query to determine if the requested AgGrid view violates
 * the mathematical boundaries set by Advanced Totaling.
 *
 * **Decision Matrix:**
 *
 * - If the UI requested a view *at or below* the formula execution boundary (e.g., expanding the exact node
 * where the formula calculates): Interception is skipped. Returns `null`. The system gracefully falls back
 * to standard, highly-optimized linear query logic.
 *
 * - If the UI requested a view *above* the execution boundary (e.g., A Grand Total, or collapsing a parent row):
 * Interception triggers. It immediately compiles an Inner / Outer query architecture, fuses them via `c.pipe`,
 * and forces the database to adhere to our strict calculation sandbox.
 *
 * @param {CascadingQueryConfig} config - Aggregated data dictionary representing all request state, metadata, and column configs.
 *
 * @returns {string | null} The fully serialized SoQL pipe query (`... |> SELECT ...`), or `null` if the pipeline is unnecessary.
 */
export const generateCascadingPipedQuery = (config: CascadingQueryConfig): string | null => {
  if (!isTableAdvancedTotalingEnabled()) {
    return null;
  }
  const evaluationLevel = getEvaluationLevel(config.hierarchyConfigs);
  if (evaluationLevel === -1) {
    return null;
  }

  // The getEvaluationLevel function above returns the index of the config with customFormula but subtracts 1.
  // Here we just add back the 1 to represent the index of the config with customFormula
  const activeConfig = config.hierarchyConfigs ? config.hierarchyConfigs[evaluationLevel + 1] : undefined;
  const hasComplexMath = activeConfig && hasMultiplyOrDivide(activeConfig);

  // We only pipe if there are multiplication or division operators in the formula
  // and if we are querying at a level above the evaluation logic
  // For context: the lower the currentQueryGroupLevel, the higher level it would be in the hierarchy inside the table.
  // Ex: Grand total level is -1, subtotals would be 0 and above)
  const requiresPipe = config.currentQueryGroupLevel < evaluationLevel && hasComplexMath;
  if (!requiresPipe) {
    return null;
  }

  const selectAliasMap = config.isExportQuery
    ? createExportSelectAliasMap(config.agRequest, config.columns, config.currentQueryGroupLevel)
    : createSelectAliasMap(config.agRequest, config.columns);

  const innerParams = buildInnerQueryParams(config, evaluationLevel, selectAliasMap);
  const outerParams = buildOuterQueryParams(config, selectAliasMap);

  return composeQuery(c.pipe(innerParams, outerParams));
};
